import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Alert } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { FadeLoader } from "react-spinners";

// Common Components
import BreadCrumb from "../../Components/Common/BreadCrumb";
import SeqTypesAndGroups from "../../Components/Common/SeqTypesAndGroups";
import ActionButton from "../../Components/Common/ActionButton";

// Page-specific Components
import GroupsManager from "../ComparisonProfile/components/GroupsManager"; 

// API
import {
  getSampleGroupingsData,
  postSampleGroupingsData,
  getPlusStatus,
} from "../../helpers/eirna_backend_helper";

// Hooks
import { useGroupsManager } from "../ComparisonProfile/hooks/useGroupsManager";


const SampleGroupings = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [plusStatus, setPlusStatus] = useState(null);  

  // API data
  const [apiData, setApiData] = useState({
    json_files: [],
    seq_types: [],
    existing_groups: {},
  });



  const {
    groups,
    addGroup,
    removeGroup,
    setGroupsFromPayload,
    updateGroupName,
    updateGroupColor,
    addFilesToGroup,
    removeFilesFromGroup,
    clearGroup,
    autoFillGroups,
    autoLabelGroups,
    clearAllGroups,
    getMasterFileDict,
  } = useGroupsManager();

  const handleAutoFillGroups = useCallback(
    (files) => {
      autoFillGroups(files, { ignoreSeqTypes: true });
    },
    [autoFillGroups]
  );

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getSampleGroupingsData(projectId);
        if (!response.existing_groups || response.status === 403) {
          toast.error("No active connect-plus subscription found for this project");
          return
        }
        setApiData(response);

        var group_idx = 1;
        // Populate any previously saved groups
        if (response.existing_groups && Object.keys(response.existing_groups).length > 0) {
          for (const key of Object.keys(response.existing_groups)) {
            console.log(`${key} = ${response.existing_groups[key]}`)
            addFilesToGroup(group_idx, response.existing_groups[key]);
            updateGroupName(group_idx, key);
            group_idx += 1;
          }

        }


        const uniqueGroups = [
          ...new Set(
            response.json_files?.map((file) => file.subgroup).filter(Boolean) ||
              []
          ),
        ];
        if (uniqueGroups.length > 0) {
          setSelectedGroup(uniqueGroups[0]);
        }

      } catch (err) {
        toast.error("Failed to load project data");
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      fetchData();
    }
  }, [projectId, setGroupsFromPayload]);

  // Filter files shown in the list based on selected subgroup only.
  // Do not filter by seq type here.
  const filteredFiles = useMemo(() => {
    let files = apiData.json_files || [];

    if (selectedGroup && selectedGroup !== "all") {
      files = files.filter((file) => file.subgroup === selectedGroup);
    }

    return files;
  }, [apiData.json_files, selectedGroup]);

  const availableGroups = useMemo(() => {
    const groupSet = new Set(
      apiData.json_files?.map((file) => file.subgroup) || []
    );

    return Array.from(groupSet)
      .filter(Boolean)
      .map((group) => ({
        value: group,
        label: group,
        count:
          apiData.json_files?.filter((file) => file.subgroup === group).length ||
          0,
      }));
  }, [apiData.json_files]);

  // Handle saving the groups to the backend
  const handleUpdateGroups = useCallback(async () => {
    try {
      setSaving(true);
      
      const masterDict = {};
      groups.forEach((group) => {
        if (group.files.length > 0) {
          masterDict[group.label] = {
            file_ids: group.files
          };
        }
      });

      // Ensure we don't send an empty payload 
      if (Object.keys(masterDict).length === 0) {
        toast.warn("No groups configured to save.");
        setSaving(false);
        return;
      }

      const payload = {
        master_file_dict: masterDict,
        project_id: projectId,
      };

      console.log("sending payload to post", payload);

      const response = await postSampleGroupingsData(payload);
      toast.success("Samples updated successfully.");
      
    } catch (err) {
      let errorMsg = "Samples failed to update.";
      if (err.response?.data?.message) {
        errorMsg += ` ${err.response.data.message}`;
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  }, [getMasterFileDict, projectId]);

  // Check Connect+ status before loading the rest of the page
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const plus_status_response = await getPlusStatus(projectId);
        setPlusStatus(plus_status_response.plus_status);
      } catch (err) {
        setError("Failed to check Connect+ access");
        setPlusStatus(false);
      }
    };

    if (projectId) {
      fetchStatus();
    }
  }, [projectId]);

  if (plusStatus === null || (plusStatus === true && loading)) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="loading-overlay">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "75vh",
              }}
            >
              <FadeLoader color="#1A6D52" size={50} />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (plusStatus === false) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="info" className="mb-0">
            This plot is part of Connect+ To discuss access please contact info@eirnabio.com
          </Alert>
        </Container>
      </div>
    );
  }

  document.title = "Sample Groupings | EIRNA Bio Connect";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            breadcrumbs={[
              { label: "Home", url: "/dashboard" },
              {
                label: "Select Function",
                url: `/projects/${projectId}/select-function`,
              },
              { label: "Sample Groupings" },
            ]}
          />

          <Row className="mb-4">
            <Col lg={12}>
              <h4
                style={{
                  fontSize: "24px",
                  fontWeight: "600",
                  color: "#1f2937",
                  marginBottom: "0",
                }}
              >
                Sample Groupings
              </h4>
              <p className="mt-2">
                Organize your project files into distinct groups/conditions to be used across various plots.
                All samples that have the same condition should be put into a single group, <b>regardless of sequence type</b>.
              </p>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col lg={12}>
              <SeqTypesAndGroups
                seqTypes={[]}
                showSeqTypes={false}
                groups={availableGroups}
                selectedSeqType="all"
                selectedGroup={selectedGroup}
                onSelectSeqType={() => {}}
                onSelectGroup={setSelectedGroup}
                showSelectAllCheckboxes={false}
                customTitle="Sample Subgroups"
                customIcon="ri-folder-2-line"
              />
            </Col>
          </Row>

          <Row className="mb-4">
            <Col lg={12}>
              <GroupsManager
                groups={groups}
                allFiles={apiData.json_files}
                filteredFiles={filteredFiles}
                onAddGroup={addGroup}
                onRemoveGroup={removeGroup}
                onUpdateGroupName={updateGroupName}
                onUpdateGroupColor={updateGroupColor}
                onAddFilesToGroup={addFilesToGroup}
                onRemoveFilesFromGroup={removeFilesFromGroup}
                onClearGroup={clearGroup}
                onAutoFill={handleAutoFillGroups}
                onAutoLabel={autoLabelGroups}
                onClearAll={clearAllGroups}
                maxGroups={64}
              />
            </Col>
          </Row>

          <Row className="mb-4">
            <Col lg={12} className="text-center">
              <ActionButton
                onClick={handleUpdateGroups}
                loading={saving}
                buttonType="save"
                customText="Update sample group info"
                customIcon="ri-save-line"
              />
            </Col>
          </Row>

          <ToastContainer limit={1} />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SampleGroupings;
